import {
  Component,
  ElementRef,
  ViewChild,
  inject,
  AfterViewInit,
  OnDestroy,
  Input,
} from '@angular/core';
import { ModalController, IonicModule, GestureController } from '@ionic/angular';
import { Gesture, GestureDetail } from '@ionic/angular';
import { Charity } from '../../../core/models/Charity';
import { PortalSwitchService } from '../../../core/services/navigation/portal-switch.service';
import { AlertService } from '../../../core/services/navigation/alert.service';
import { LoggerService } from '../../../core/services/logger/logger.service';

/**
 * "Switch Charity" sheet for accounts that manage more than one charity.
 *
 * The switch itself goes through `PortalSwitchService`, which waits for the charity context to
 * load (with a timeout) and then navigates. An earlier version subscribed to `charity$` by hand
 * and unsubscribed from inside `next`; because `charity$` replays the current charity
 * synchronously on subscribe, picking the charity that was already loaded threw a
 * `ReferenceError` ("Cannot access 'charitySubscription' before initialization") and surfaced as
 * a "Something went wrong" toast on every switch after the first.
 *
 * Ids are compared as strings: the permitted-charities payload carries numeric ids while the
 * active context id comes from storage as a string.
 */
@Component({
  selector: 'app-charity-selector-bottom-sheet',
  templateUrl: './charity-selector-bottom-sheet.component.html',
  styleUrls: ['./charity-selector-bottom-sheet.component.scss'],
  imports: [IonicModule],
})
export class CharitySelectorBottomSheetComponent implements AfterViewInit, OnDestroy {
  @ViewChild('gestureArea', { read: ElementRef }) gestureArea!: ElementRef;

  private modalController = inject(ModalController);
  private gestureController = inject(GestureController);
  private portalSwitchService = inject(PortalSwitchService);
  private alertService = inject(AlertService);
  private logger = inject(LoggerService);

  @Input() permittedCharities: Charity[] = [];
  @Input() currentCharityId: string | number | null = null;
  isSwitching: boolean = false;
  imageLoadedMap: { [key: string]: boolean } = {};
  private gesture!: Gesture;

  ngAfterViewInit() {
    this.setupGesture();
  }

  ngOnDestroy() {
    if (this.gesture) {
      this.gesture.destroy();
    }
  }

  setupGesture() {
    if (!this.gestureArea) return;

    this.gesture = this.gestureController.create({
      el: this.gestureArea.nativeElement,
      threshold: 15,
      gestureName: 'swipe-down',
      direction: 'y',
      onMove: (detail: GestureDetail) => this.onMove(detail),
      onEnd: (detail: GestureDetail) => this.onEnd(detail),
    });
    this.gesture.enable();
  }

  onMove(detail: GestureDetail) {
    if (detail.deltaY > 0) {
      this.gestureArea.nativeElement.style.transform = `translateY(${detail.deltaY}px)`;
    }
  }

  onEnd(detail: GestureDetail) {
    const shouldClose: boolean = detail.deltaY > 100;

    if (shouldClose) {
      this.modalController.dismiss({ selected: false });
    } else {
      this.gestureArea.nativeElement.style.transform = '';
    }
  }

  /** True for the charity that is already the active portal context. */
  isCurrent(charity: Charity): boolean {
    const id: string = this.idOf(charity.id);
    return id !== '' && id === this.idOf(this.currentCharityId);
  }

  async selectCharity(charity: Charity): Promise<void> {
    const targetCharityId: string = this.idOf(charity.id);
    if (this.isSwitching || !targetCharityId) {
      return;
    }

    if (this.isCurrent(charity)) {
      // Already managing this charity: just return to the portal.
      await this.modalController.dismiss({ selected: true });
      await this.portalSwitchService.switchToCharity(targetCharityId);
      return;
    }

    this.isSwitching = true;
    try {
      const switched: boolean = await this.portalSwitchService.switchToCharity(
        targetCharityId,
        async () => {
          await this.modalController.dismiss({ selected: true });
        },
      );
      if (!switched) {
        this.logger.warn('Charity switch from selector sheet did not complete', {
          targetCharityId,
        });
        this.alertService.showToast("Couldn't switch charities. Please try again.", 'danger');
      }
    } finally {
      this.isSwitching = false;
    }
  }

  onImageLoad(charityId: string) {
    this.imageLoadedMap[charityId] = true;
  }

  cancel() {
    this.modalController.dismiss({ selected: false });
  }

  private idOf(id: string | number | null | undefined): string {
    return id === null || id === undefined ? '' : String(id);
  }
}
